import apiClient, { getErrorMessage } from './api.service';
import { API_ENDPOINTS } from '../config/api';

export interface AdminUser {
  _id: string;
  name: string;
  email: string;
  role: string;
  gender: string;
  studentId?: string;
  phoneNumber?: string;
  emergencyContactName?: string;
  emergencyContactNumber?: string;
  isVerified: boolean;
  createdAt: string;
  updatedAt: string;
  bookingCount: number;
}

export interface UserStats {
  total: number;
  verified: number;
  unverified: number;
  male: number;
  female: number;
  admins: number;
  students: number;
  recentRegistrations: number;
}

export interface UserDetailsResponse {
  user: AdminUser;
  bookings: any[];
  activityLogs: any[];
  stats: {
    totalBookings: number;
    activeBookings: number;
    pendingBookings: number;
  };
}

export interface PaymentDetails {
  bankName?: string;
  accountNumber?: string;
  accountName?: string;
  branch?: string;
  momoNumber?: string;
  momoName?: string;
}

export interface ContactDetails {
  email?: string;
  phone?: string;
  whatsapp?: string;
  address?: string;
}

export interface BookingConfig {
  bookingEnabled: boolean;
  bookingClosedMessage: string;
  pricePerAcademicYear?: number;
  momoSurchargePercentage?: number;
  academicYearMoveInDate?: string;
  paymentDetails?: PaymentDetails;
  contactDetails?: ContactDetails;
  bookingRules?: { securityDeposit?: number };
}

export interface GetUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  gender?: string;
  isVerified?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

class AdminService {
  /**
   * Get the site booking configuration (readable by any authenticated user).
   */
  async getConfig(): Promise<BookingConfig> {
    try {
      const response = await apiClient.get<{
        status: string;
        data: { config: BookingConfig };
      }>(API_ENDPOINTS.ADMIN.GET_CONFIG);

      return response.data.data.config;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Update the booking configuration (admin only).
   */
  async updateConfig(payload: Partial<BookingConfig>): Promise<BookingConfig> {
    try {
      const response = await apiClient.patch<{
        status: string;
        data: { config: BookingConfig };
      }>(API_ENDPOINTS.ADMIN.UPDATE_CONFIG, payload);

      return response.data.data.config;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Get all users with filters and pagination
   */
  async getUsers(params: GetUsersParams = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          queryParams.append(key, value.toString());
        }
      });

      const response = await apiClient.get<{
        status: string;
        data: {
          users: AdminUser[];
          pagination: {
            total: number;
            page: number;
            limit: number;
            pages: number;
          };
        };
      }>(`${API_ENDPOINTS.ADMIN.GET_USERS}?${queryParams.toString()}`);

      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Get detailed user information
   */
  async getUserDetails(userId: string) {
    try {
      const response = await apiClient.get<{
        status: string;
        data: UserDetailsResponse;
      }>(API_ENDPOINTS.ADMIN.GET_USER_DETAILS(userId));

      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Update a user's role (promote to admin / demote to student)
   */
  async updateUserRole(userId: string, role: 'admin' | 'student') {
    try {
      const response = await apiClient.patch<{
        status: string;
        data: { user: AdminUser };
      }>(API_ENDPOINTS.ADMIN.UPDATE_USER_ROLE(userId), { role });

      return response.data.data.user;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Get user statistics
   */
  async getUserStats() {
    try {
      const response = await apiClient.get<{
        status: string;
        data: UserStats;
      }>(API_ENDPOINTS.ADMIN.GET_USER_STATS);

      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Export users to CSV
   */
  async exportUsersToCSV(params: Omit<GetUsersParams, 'page' | 'limit' | 'sortBy' | 'sortOrder'> = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          queryParams.append(key, value.toString());
        }
      });

      const response = await apiClient.get(
        `${API_ENDPOINTS.ADMIN.EXPORT_USERS_CSV}?${queryParams.toString()}`,
        {
          responseType: 'blob'
        }
      );

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `users-export-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }
}

const adminService = new AdminService();
export default adminService;
